import { ApiBaseService } from "./ApiBaseService";

export interface SearchCompanyOptions {
    website?: string;
    exact_website?: string;
    size?: string;
    industry?: string;
    country?: string;
    created_at_gte?: string;
    created_at_lte?: string;
    last_updated_gte?: string;
    last_updated_lte?: string;
    employees_count_gte?: number;
    employees_count_lte?: number;
    source_id?: number;
    founded_year_gte?: number;
    founded_year_lte?: number;
    funding_total_rounds_count_gte?: number;
    funding_total_rounds_count_lte?: number;
    funding_last_round_type?: string;
    funding_last_round_date_gte?: string;
    funding_last_round_date_lte?: string;
}

export interface CompanyAffiliated {
    id: number;
    company_id: number;
    affiliated_company_url: string;
    affiliated_company_id: number | null;
    created: string;
    last_updated: string;
    deleted: number;
}

export interface CompanyAlsoViewed {
    id: number;
    company_id: number;
    viewed_company_url: string;
    viewed_company_id: number | null;
    created: string;
    last_updated: string;
    deleted: number;
}

export interface CompanyFeaturedEmployee {
    id: number;
    company_id: number;
    url: string;
    created: string;
    last_updated: string;
    deleted: number;
}

export interface CompanyLocation {
    id: number;
    company_id: number;
    location_address: string;
    is_primary: number;
    created: string;
    last_updated: string;
    deleted: number;
}

export interface CompanySimilar {
    id: number;
    company_id: number;
    url: string;
    created: string;
    last_updated: string;
    deleted: number;
}

export interface CompanySpecialty {
    id: number;
    company_id: number;
    specialty: string;
    created: string;
    last_updated: string;
    deleted: number;
}

export interface CompanyUpdate {
    id: number;
    company_id: number;
    urn: string;
    followers: number;
    date: string;
    description: string;
    reactions_count: number;
    comments_count: number | null;
    reshared_post_author: string;
    reshared_post_author_url: string;
    reshared_post_author_headline: string | null;
    reshared_post_description: string;
    reshared_post_followers: number;
    reshared_post_date: string;
    last_updated: string;
    deleted: number;
}

export interface Company {
    id: number;
    url: string;
    hash: string;
    name: string;
    website: string;
    size: string;
    industry: string;
    description: string;
    followers: number;
    founded: number;
    headquarters_city: string | null;
    headquarters_country: string | null;
    headquarters_state: string | null;
    headquarters_street1: string | null;
    headquarters_street2: string | null;
    headquarters_zip: string | null;
    logo_url: string;
    created: string;
    last_updated: string;
    last_response_code: number;
    type: string;
    headquarters_new_address: string;
    employees_count: number;
    headquarters_country_restored: string | null;
    headquarters_country_parsed: string;
    company_shorthand_name: string;
    company_shorthand_name_hash: string;
    canonical_url: string;
    canonical_hash: string;
    canonical_shorthand_name: string;
    canonical_shorthand_name_hash: string;
    deleted: number;
    last_updated_ux: number;
    source_id: number;
    company_affiliated_collection: CompanyAffiliated[];
    company_also_viewed_collection: CompanyAlsoViewed[];
    company_crunchbase_info_collection: any[];
    company_featured_employees_collection: CompanyFeaturedEmployee[];
    company_featured_investors_collection: any[];
    company_funding_rounds_collection: any[];
    company_locations_collection: CompanyLocation[];
    company_similar_collection: CompanySimilar[];
    company_specialties_collection: CompanySpecialty[];
    company_stock_info_collection: any[];
    company_updates_collection: CompanyUpdate[];
}

export const industries = ref<string[]>([
    "Human Resources Services",
    "Staffing & Recruiting",
    "Information Technology & Services",
    "Software Development",
    "IT Services and IT Consulting",
    "Industrial Machinery Manufacturing",
    "Business Consulting and Services",
    "Apparel & Fashion",
    "Farming",
    "Banking",
    "Judiciary",
    "Photography",
    "Airlines/Aviation",
    "Telecommunications",
    "Printing",
    "Machinery",
    "Textiles",
    "Hospitality",
    "Transportation/Trucking/Railroad",
    "Retail",
    "Utilities",
    "Construction",
    "Supermarkets",
    "Research",
    "Sports",
    "Internet",
    "Accounting",
    "Insurance",
    "Automotive",
    "Semiconductors",
    "Warehousing",
    "Restaurants",
    "Entertainment",
    "Cosmetics",
    "Chemicals",
    "Biotechnology",
    "Philanthropy",
    "Wholesale",
    "Design",
    "Ranching",
    "Libraries",
    "Dairy",
    "Newspapers",
    "Maritime",
    "Pharmaceuticals",
    "Military",
    "Outsourcing/Offshoring",
    "Fishery",
    "Furniture",
    "Animation",
    "Publishing",
    "Plastics",
    "Other",
    "Wireless",
    "Music",
    "E-learning",
    "Veterinary",
    "Shipbuilding",
    "Fundraising",
    "Tobacco",
    "Nanotechnology",
    "Think Tanks",
    "Public Relations & Communications",
    "Mechanical Or Industrial Engineering",
    "Import & Export",
    "Arts & Crafts",
    "Computer Hardware",
    "Electrical & Electronic Manufacturing",
    "Consumer Electronics",
    "Human Resources",
    "Civil Engineering",
    "Capital Markets",
    "Non-profit Organization Management",
    "Financial Services",
    "Packaging & Containers",
    "Computer Software",
    "Alternative Medicine",
    "Luxury Goods & Jewelry",
    "Consumer Services",
    "Industrial Automation",
    "Computer & Network Security",
    "Civic & Social Organization",
    "Facilities Services",
    "Medical Practice",
    "Primary/Secondary Education",
    "Broadcast Media",
    "Marketing & Advertising",
    "Health, Wellness & Fitness",
    "Logistics & Supply Chain",
    "Business Supplies & Equipment",
    "Real Estate",
    "Information Services",
    "Education Management",
    "Consumer Goods",
    "Food & Beverages",
    "Law Practice",
    "Government Administration",
    "Management Consulting",
    "Law Enforcement",
    "Building Materials",
    "Executive Office",
    "Political Organization",
    "Government Relations",
    "Renewables & Environment",
    "Investment Management",
    "Hospital & Health Care",
    "Glass, Ceramics & Concrete",
    "Higher Education",
    "Program Development",
    "Oil & Energy",
    "International Affairs",
    "Fine Art",
    "International Trade & Development",
    "Mining & Metals",
    "Medical Device",
    "Food Production",
    "Market Research",
    "Paper & Forest Products",
    "Computer Networking",
    "Defense & Space",
    "Writing & Editing",
    "Graphic Design",
    "Computer Games",
    "Environmental Services",
    "Security & Investigations",
    "Venture Capital & Private Equity",
    "Aviation & Aerospace",
    "Public Policy",
    "Events Services",
    "Public Safety",
    "Architecture & Planning",
    "Package/Freight Delivery",
    "Leisure, Travel & Tourism",
    "Commercial Real Estate",
    "Individual & Family Services",
    "Investment Banking",
    "Sporting Goods",
    "Professional Training & Coaching",
    "Legal Services",
    "Recreational Facilities & Services",
    "Legislative Office",
    "Religious Institutions",
    "Mental Health Care",
    "Online Media",
    "Wine & Spirits",
    "Media Production",
    "Performing Arts",
    "Motion Pictures & Film",
    "Museums & Institutions",
    "Translation & Localization",
    "Alternative Dispute Resolution",
    "Gambling & Casinos",
    "Railroad Manufacture",
]);

export default class CoreSignalService extends ApiBaseService {
    public companies() {
        const search = (
            name: string,
            industry?: string[],
            country?: string[],
            options: Partial<SearchCompanyOptions> = {},
        ) => {
            return this.request(
                this.route(
                    "api.core-signal.companies",
                    "core-signal/companies",
                ),
                {
                    method: "POST",
                    body: {
                        ...options,
                        name,
                        industry,
                        country,
                    },
                },
            );
        };

        const show = (body: {
            id?: number;
            brand_id?: number;
            industry?: string[];
            country?: string[];
        }) => {
            return this.request(
                this.route(
                    "api.core-signal.companies.show",
                    "core-signal/companies/show",
                ),
                {
                    method: "POST",
                    body,
                },
            );
        };

        return {
            search,
            show,
        };
    }

    public members() {
        const search = (options: Partial<any> = {}) => {
            return this.request(
                this.route("api.core-signal.members", "core-signal/members"),
                {
                    method: "POST",
                    body: {
                        ...options,
                    },
                },
            );
        };

        return {
            search,
        };
    }
}
